import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { selectCurrentUser } from "../../../features/auth/store/authSlice";
import { getClientById } from "../../../core/api/clientsService";

const Header = ({ handleSignOut, isActionLoading }) => {
  const userData = useSelector(selectCurrentUser) || {};
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [fetchedClientName, setFetchedClientName] = useState("");
  const dropdownRef = useRef(null);

  const clientId = userData.client_id ?? null;
  // Prefer the org name already on the session; only hit the API when the
  // logged-in user record doesn't carry it.
  const orgName = userData.client_name || fetchedClientName;

  // Resolve the organization name from its id when it's missing on the user.
  useEffect(() => {
    if (userData.client_name || !clientId) {
      setFetchedClientName("");
      return;
    }

    let isActive = true;
    (async () => {
      const client = await getClientById(clientId);
      if (isActive && client && !client.error) {
        setFetchedClientName(client.client_name || client.name || "");
      }
    })();

    return () => {
      isActive = false;
    };
  }, [clientId, userData.client_name]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getUserDisplayName = () => {
    const first = userData.firstname || "";
    const last = userData.lastname || "";
    if (first || last) return `${first} ${last}`.trim();
    return userData.email ? userData.email.split("@")[0] : "";
  };

  const getUserInitials = () => {
    const first = userData.firstname;
    const last = userData.lastname;
    if (first && last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    if (first) return first.charAt(0).toUpperCase();
    if (userData.email) return userData.email.charAt(0).toUpperCase();
    return "";
  };

  return (
    <header className="flex items-center justify-between border-b border-line bg-white sm:h-20 px-4 sm:px-6 lg:px-8 py-5">
      {/* Left side - User Profile */}
      <div className="flex items-center gap-2 sm:gap-4">
        <div className="hidden lg:flex items-center gap-4">
          <div className="relative flex-shrink-0">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary flex items-center justify-center">
              <span className="text-white text-lg sm:text-2xl font-bold">
                {orgName ? orgName.charAt(0).toUpperCase() : ""}
              </span>
            </div>
            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-successBright rounded-full border-2 border-white"></div>
          </div>
          <div className="flex flex-col min-w-0">
            <p className="text-primary text-base sm:text-lg md:text-xl font-bold leading-tight truncate">
              {orgName || "—"}
            </p>
            <p className="text-muted text-xs sm:text-sm md:text-sm leading-tight truncate">
              Organization
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Icons and User Profile */}
      <div className="flex items-center min-w-0">
        {/* User Profile with Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3">
            <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 min-w-0">
              <div className="relative flex-shrink-0">
                <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-primary flex items-center justify-center overflow-hidden">
                  {/* <Avatar /> */}
                  <span className="text-white text-sm sm:text-base md:text-md lg:text-lg font-bold leading-3">
                    {getUserInitials()}
                  </span>
                </div>
                <div className="absolute bottom-0 right-0 w-2.5 h-2.5 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 bg-successBright rounded-full border-2 border-white"></div>
              </div>
              <div className="flex flex-col min-w-0 sm:flex">
                <p className="text-primary text-sm sm:text-base md:text-md lg:text-lg font-bold leading-tight truncate">
                  {getUserDisplayName()}
                </p>
                <p className="text-content text-xs sm:text-sm md:text-base lg:text-md leading-tight truncate max-w-[120px] sm:max-w-[150px] md:max-w-none">
                  {userData.email || ""}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="text-primary hover:opacity-70 transition-opacity flex-shrink-0 p-1"
              aria-label="Toggle user menu"
            >
              <KeyboardArrowDownIcon
                className={`transition-transform text-lg sm:text-xl md:text-2xl ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
              <button
                onClick={() => {
                  handleSignOut();
                  setIsDropdownOpen(false);
                }}
                disabled={isActionLoading}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-content hover:bg-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <LogoutIcon className="text-content" fontSize="small" />
                <span>{isActionLoading ? "Logging out..." : "Log Out"}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
